import { lazy, type ComponentType } from 'react';

/**
 * Chunk-load resilience for lazy routes (BUG-141).
 *
 * Every page is a `lazy()` route under one top-level <Suspense> (router.tsx).
 * When a route chunk fails to load, that single Suspense fallback (a centered
 * spinner above the whole shell) is what the user sees — so a broken chunk
 * blanks the entire app, not just the page body, and fires no further requests
 * (the shell never mounts). Two failure modes do this:
 *
 *   - reject — the chunk request fails outright (e.g. the asset 404s after a
 *     deploy rotated the build hashes while this tab still held a stale
 *     index.html). React.lazy re-throws on the next render → error boundary.
 *   - stall  — the chunk request never settles (a hung asset response behind
 *     nginx; aggravated by hashed assets served with 304-revalidation instead
 *     of `immutable`). `import()` stays pending, so React keeps the Suspense
 *     fallback up forever with NO error and NO recovery. This is the spinner
 *     the operator hits "периодически".
 *
 * A timeout converts the stall into a reject; on either, we do ONE full reload
 * to fetch the current index.html + chunk map. A sessionStorage flag makes the
 * reload un-loopable: if the import still fails after the reload, the flag is
 * already set and we rethrow to the error path instead of reloading again. The
 * flag is cleared on the first successful import so a later deploy re-arms it.
 *
 * Mirrors apps/player's `lazyWithReload` (BUG-114), plus the timeout — the
 * admin has no service worker, so the stall mode has no other safety net.
 * nginx `immutable` caching on /assets is the recommended infra follow-up.
 */

const CHUNK_RELOAD_FLAG = 'admin-chunk-reload';

// A route chunk is a few tens of KB; >12s means the request is hung, not slow.
const IMPORT_TIMEOUT_MS = 12_000;

function reloadOnce(): boolean {
  if (sessionStorage.getItem(CHUNK_RELOAD_FLAG)) return false;
  sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
  window.location.reload();
  return true;
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`dynamic import timed out after ${ms}ms`)),
      ms,
    );
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

export function lazyWithReload<T extends ComponentType<Record<string, never>>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    withTimeout(factory(), IMPORT_TIMEOUT_MS)
      .then((mod) => {
        sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
        return mod;
      })
      .catch((error: unknown) => {
        if (reloadOnce()) {
          // Reload is in flight — keep the Suspense fallback mounted until the
          // page is replaced, instead of throwing an opaque chunk error at the
          // user. Never resolves; the navigation away discards it.
          return new Promise<{ default: T }>(() => {});
        }
        throw error;
      }),
  );
}

/**
 * Vite fires `vite:preloadError` when its module-preload helper cannot fetch a
 * chunk (typically a post-deploy hash that no longer exists). Register once
 * from the app entry: same guarded one-time reload as `lazyWithReload`, sharing
 * CHUNK_RELOAD_FLAG so the two paths never reload twice.
 */
export function registerChunkReloadHandler(): void {
  window.addEventListener('vite:preloadError', () => {
    reloadOnce();
  });
}
